import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Container, Paper, Tooltip, Typography } from '@mui/material';
import OverviewIcon from '@mui/icons-material/QueryStats';
import { EMPLOYEE_GROUP_ID, useAuth } from '../lib/auth';
import { formatLongDate, getGreeting } from '../lib/time';
import { groupModules } from '../config/nav';
import MyDayIcon from '@mui/icons-material/PhoneIphone';
import { ModuleTile, SectionLabel, StatCard } from '../components/primitives';
import { ACCENTS, navBarGradient } from '../lib/theme';
import { useThemeId } from '../lib/theme-context';
import { StatusPill } from '../components/StatusPill';
import { WeatherBanner } from '../components/WeatherBanner';
import { apiFetch } from '../lib/api';
import { LeaveRequestsDialog } from '../components/timesheet/LeaveRequestsDialog';

/**
 * Dashboard — the landing page and the design reference for the rest of the app.
 *
 * Structure: greeting strip, an overview banner holding the stat cards, then the
 * module tiles grouped into labelled sections. Tiles are filtered by permission,
 * so a staff member only sees what they can actually open.
 */

interface UninvoicedSummary {
    readonly entries: number;
    readonly jobs: number;
    readonly hours: number;
    readonly value: number;
    readonly unpricedEntries: number;
}

interface StaffOnJobSummary {
    readonly onJob: number;
    readonly finished: number;
}

interface LeaveSummary {
    readonly pending: number;
    readonly upcoming: number;
}

interface JobSummary {
    readonly active: number;
    readonly activeThisMonth: number;
}

/** Whole dollars: the card has room for "$33,934", not cents. */
const WHOLE_DOLLARS = new Intl.NumberFormat('en-AU', {
    style: 'currency',
    currency: 'AUD',
    maximumFractionDigits: 0,
});

export function Dashboard(): React.JSX.Element {
    const navigate = useNavigate();
    const { me, modules, can } = useAuth();
    const isFieldStaff = me?.staffGroupId === EMPLOYEE_GROUP_ID;
    const canInvoice = can('outstanding-invoicing');
    const canJobs = can('jobs');
    const canTimesheet = can('timesheet');
    const [leaveSummary, setLeaveSummary] = useState<LeaveSummary | null>(null);
    const [leaveOpen, setLeaveOpen] = useState(false);
    const [leaveNonce, setLeaveNonce] = useState(0);
    const [staffSummary, setStaffSummary] = useState<StaffOnJobSummary | null>(null);
    const [jobSummary, setJobSummary] = useState<JobSummary | null>(null);
    const [uninvoiced, setUninvoiced] = useState<UninvoicedSummary | null>(null);
    const { themeId } = useThemeId();
    const [now, setNow] = useState(() => new Date());

    // The date line only needs to change when the day does; a minute is ample
    // and avoids re-rendering the whole tile grid every second.
    useEffect(() => {
        const t = setInterval(() => {
            setNow(new Date());
        }, 60_000);
        return () => {
            clearInterval(t);
        };
    }, []);

    useEffect(() => {
        if (!canInvoice) {
            return;
        }
        let cancelled = false;
        apiFetch<UninvoicedSummary>('/outstanding-invoicing/summary')
            .then((r) => {
                if (!cancelled) {
                    setUninvoiced(r);
                }
            })
            .catch(() => {
                /* The card keeps its dashes; the dashboard still works. */
            });
        return () => {
            cancelled = true;
        };
    }, [canInvoice]);

    useEffect(() => {
        if (!canJobs) {
            return;
        }
        let cancelled = false;
        apiFetch<JobSummary>('/jobs/summary')
            .then((r) => {
                if (!cancelled) {
                    setJobSummary(r);
                }
            })
            .catch(() => {
                /* The card keeps its dashes. */
            });
        return () => {
            cancelled = true;
        };
    }, [canJobs]);

    useEffect(() => {
        if (!canTimesheet) {
            return;
        }
        let cancelled = false;
        apiFetch<StaffOnJobSummary>('/timesheets/staff-summary')
            .then((r) => {
                if (!cancelled) {
                    setStaffSummary(r);
                }
            })
            .catch(() => {
                /* The card keeps its dashes. */
            });
        return () => {
            cancelled = true;
        };
    }, [canTimesheet]);

    useEffect(() => {
        if (!canTimesheet) {
            return;
        }
        let cancelled = false;
        apiFetch<LeaveSummary>('/timesheets/leave-requests/summary')
            .then((r) => {
                if (!cancelled) {
                    setLeaveSummary(r);
                }
            })
            .catch(() => {
                /* The card keeps its dashes. */
            });
        return () => {
            cancelled = true;
        };
    }, [canTimesheet, leaveNonce]);

    const groups = groupModules(modules);

    return (
        <Container maxWidth="xl" sx={{ pt: { xs: 1.5, md: 2 }, pb: { xs: 2, md: 4 }, px: { xs: 2, md: 3 } }}>
            {/* ─── Header strip: greeting, status, weather ────────────────
                One continuous row on desktop, wrapping on narrower screens.
                The dividers belong to WeatherBanner so the spacing stays
                correct when weather is unavailable and the banner renders
                nothing at all. */}
            <Box
                sx={{
                    mb: 2,
                    px: { xs: 0.5, md: 1 },
                    py: 1,
                    display: 'flex',
                    alignItems: 'center',
                    flexWrap: { xs: 'wrap', lg: 'nowrap' },
                    gap: { xs: 2, lg: 0 },
                }}
            >
                <Box sx={{ minWidth: 'fit-content', mr: { lg: 3 } }}>
                    <Typography
                        sx={{
                            fontWeight: 800,
                            fontSize: { xs: '1.15rem', md: '1.35rem' },
                            lineHeight: 1.2,
                            color: 'text.primary',
                        }}
                    >
                        {getGreeting(now)}
                    </Typography>
                    <Typography sx={{ color: 'text.secondary', fontSize: '0.75rem', opacity: 0.8 }}>
                        {me?.staffName ?? ''}
                    </Typography>
                    <Typography sx={{ color: 'text.secondary', fontSize: '0.7rem', opacity: 0.6 }}>
                        {formatLongDate(now)}
                    </Typography>
                </Box>

                <StatusPill />

                {/* Below lg the weather takes its own full-width line. With a
                    plain flex: 1 it was squeezed to zero width beside the
                    greeting on a phone and its contents spilled off-screen. */}
                <Box sx={{ flex: { xs: '1 1 100%', lg: 1 }, minWidth: 0 }}>
                    <WeatherBanner />
                </Box>
            </Box>

            {/* ─── Overview banner ────────────────────────────────────────── */}
            <Paper
                sx={{
                    background: navBarGradient(themeId),
                    borderRadius: 2,
                    color: 'common.white',
                    p: 2,
                    mb: 3,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 1,
                }}
            >
                <Typography variant="subtitle1" sx={{ display: 'flex', alignItems: 'center', gap: 1, fontWeight: 800 }}>
                    <OverviewIcon fontSize="small" />
                    Today at a glance
                </Typography>
                <Typography sx={{ fontSize: '0.7rem', fontStyle: 'italic', color: 'rgba(255,255,255,0.7)', mb: 1 }}>
                    Live figures arrive with the Calendar and Timesheet modules
                </Typography>

                <Box
                    sx={{
                        display: 'grid',
                        gap: 1.5,
                        gridTemplateColumns: { xs: '1fr', md: 'repeat(auto-fit, minmax(190px, 1fr))' },
                    }}
                >
                    {/* Placeholders until the Calendar and Timesheet modules land.
                        Shown rather than hidden so the dashboard layout is real and
                        reviewable now; each is wired up in its own phase. */}
                    <StatCard
                        title="Scheduled"
                        icon={<OverviewIcon fontSize="small" />}
                        colour={ACCENTS.blue}
                        metrics={[
                            { label: 'Today', value: '—' },
                            { label: 'This week', value: '—' },
                        ]}
                    />
<StatCard
                        title="Staff on job"
                        icon={<OverviewIcon fontSize="small" />}
                        colour={ACCENTS.green}
                        metrics={[
                            // Clocked on today and not yet clocked off.
                            { label: 'On job', value: staffSummary === null ? '—' : String(staffSummary.onJob) },
                            { label: 'Finished', value: staffSummary === null ? '—' : String(staffSummary.finished) },
                        ]}
                        {...(canTimesheet && {
                            onClick: () => {
                                void navigate('/timesheet');
                            },
                        })}
                    />
                    <StatCard
                        title="Open jobs"
                        icon={<OverviewIcon fontSize="small" />}
                        colour={ACCENTS.amber}
                        metrics={[
                            { label: 'Active', value: jobSummary === null ? '—' : String(jobSummary.active) },
                            {
                                label: 'This month',
                                value: jobSummary === null ? '—' : String(jobSummary.activeThisMonth),
                            },
                        ]}
                        {...(canJobs && {
                            onClick: () => {
                                void navigate('/jobs');
                            },
                        })}
                    />
                    <Tooltip
                        title={
                            uninvoiced === null
                                ? ''
                                : `${String(uninvoiced.entries)} timesheet entries, ${uninvoiced.hours.toFixed(2)} hours. ` +
                                  'Value is hours × client rate, ex GST' +
                                  (uninvoiced.unpricedEntries > 0
                                      ? `; ${String(uninvoiced.unpricedEntries)} entries have no client rate and are not included.`
                                      : '.')
                        }
                    >
                        <Box>
                            <StatCard
                                title="Uninvoiced"
                                icon={<OverviewIcon fontSize="small" />}
                                colour={ACCENTS.pink}
                                metrics={[
                                    { label: 'Jobs', value: uninvoiced === null ? '—' : String(uninvoiced.jobs) },
                                    {
                                        label: 'Value',
                                        value: uninvoiced === null ? '—' : WHOLE_DOLLARS.format(uninvoiced.value),
                                    },
                                ]}
                                {...(canInvoice && {
                                    onClick: () => {
                                        void navigate('/outstanding-invoicing');
                                    },
                                })}
                            />
                        </Box>
                    </Tooltip>
                    <Tooltip title="Pending: waiting for a decision. Upcoming: approved leave on now or in the next 14 days.">
                        <Box>
                            <StatCard
                                title="Leave requests"
                                icon={<OverviewIcon fontSize="small" />}
                                colour={ACCENTS.indigo}
                                metrics={[
                                    { label: 'Pending', value: leaveSummary === null ? '—' : String(leaveSummary.pending) },
                                    {
                                        label: 'Upcoming',
                                        value: leaveSummary === null ? '—' : String(leaveSummary.upcoming),
                                    },
                                ]}
                                {...(canTimesheet && {
                                    onClick: () => {
                                        setLeaveOpen(true);
                                    },
                                })}
                            />
                        </Box>
                    </Tooltip>
                </Box>
            </Paper>

            {/* ─── My Day ─────────────────────────────────────────────────
                Only for the Employee role. They land on /my (see App.tsx) and
                reach this page through "Office view", so the tile is their way
                back. Office and admin staff do not clock on from a phone, so
                for them it is a tile that leads somewhere they will never use.

                The ROUTE stays open to everyone — /my is scoped to whoever is
                signed in and an admin may legitimately want to look at it. This
                is about not putting it in front of people who do not need it. */}
            {isFieldStaff ? (
            <Box sx={{ mb: 3 }}>
                <SectionLabel>Me</SectionLabel>
                <Box
                    sx={{
                        display: 'grid',
                        gap: 2,
                        gridTemplateColumns: {
                            xs: '1fr',
                            sm: 'repeat(2, 1fr)',
                            md: 'repeat(3, 1fr)',
                            lg: 'repeat(4, 1fr)',
                        },
                    }}
                >
                    <ModuleTile
                        label="My Day"
                        description="Clock on and off, your roster, leave and SWMS"
                        icon={<MyDayIcon fontSize="large" />}
                        colour={ACCENTS.green}
                        onClick={() => {
                            void navigate('/my');
                        }}
                    />
                </Box>
            </Box>
            ) : null}

            {/* ─── Module tiles ───────────────────────────────────────────── */}
            {groups.map((group) => (
                <Box key={group.heading} sx={{ mb: 3 }}>
                    <SectionLabel>{group.heading}</SectionLabel>
                    <Box
                        sx={{
                            display: 'grid',
                            gap: 2,
                            gridTemplateColumns: {
                                xs: '1fr',
                                sm: 'repeat(2, 1fr)',
                                md: 'repeat(3, 1fr)',
                                lg: 'repeat(4, 1fr)',
                            },
                        }}
                    >
                        {group.items.map((module) => (
                            <ModuleTile
                                key={module.slug}
                                label={module.label}
                                description={module.description}
                                icon={module.icon}
                                colour={module.colour}
                                available={module.available ?? true}
                                onClick={() => {
                                    void navigate(module.path);
                                }}
                            />
                        ))}
                    </Box>
                </Box>
            ))}

            {canTimesheet ? (
                <LeaveRequestsDialog
                    open={leaveOpen}
                    onClose={() => {
                        setLeaveOpen(false);
                    }}
                    onDecided={() => {
                        setLeaveNonce((n) => n + 1);
                    }}
                />
            ) : null}

            {groups.length === 0 ? (
                <Typography sx={{ color: 'text.disabled', fontStyle: 'italic', mt: 4 }}>
                    You don&apos;t have access to any modules yet. Ask an administrator to set your staff group.
                </Typography>
            ) : null}
        </Container>
    );
}
